import React, { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { site } from "@/data/enrichments";

const NAV = [
  { to: "/books", label: "Books" },
  { to: "/dharma", label: "App" },
  { to: "/writing", label: "Writing" },
  { to: "/listen", label: "Listen" },
  { to: "/watch", label: "Films" },
  { to: "/shop", label: "Shop" },
  { to: "/follow", label: "Follow" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-slow ease-breath ${
        scrolled
          ? "bg-cream/85 backdrop-blur-md border-b border-dharma-gold/15"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14 py-4 md:py-5 flex items-center justify-between">
        <Link to="/" className="group flex items-center gap-2.5 md:gap-3">
          <img
            src={site.logo_icon}
            alt=""
            className="h-7 md:h-8 w-auto select-none"
            draggable={false}
          />
          <span className="font-display text-base sm:text-lg md:text-xl tracking-widest2 text-ink whitespace-nowrap">
            DHARMAWEAVE
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-3.5 xl:gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `relative font-body text-[0.78rem] tracking-widest2 uppercase transition-colors duration-500 ease-breath ${
                  isActive
                    ? "text-dharma-gold-deep"
                    : "text-ink-soft hover:text-dharma-gold-deep"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-dharma-gold transition-all duration-500 ease-breath ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </div>

        <button
          className="lg:hidden p-3 -mr-3 text-ink-soft hover:text-dharma-gold-deep transition-colors"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-cream/95 backdrop-blur-md border-b border-dharma-gold/15"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `font-body text-sm tracking-widest2 uppercase py-3 ${
                      isActive ? "text-dharma-gold-deep" : "text-ink-soft"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
